import type { VariableMeta } from '@tsagko/label-contracts'
import { useEffect, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { VariableField } from './VariableField'

export interface VariableGroup {
  zoneId: string
  path: string[]
  blockName: string
  blockVersion: number
  /** Appended to the heading only to disambiguate duplicate blocks (alignment-based). */
  placementLabel?: string
  /** Loaded variable metadata; `undefined` means the block def was not loaded yet or failed. */
  variables?: Record<string, VariableMeta>
  /** True when the block def load attempt failed (distinct from "not yet loaded"). */
  loadFailed?: boolean
  /** Current values, sourced from `BakedBlockInstance.props`. */
  values: Record<string, string>
}

interface TemplateVariablesFormProps {
  groups: VariableGroup[]
  onCommit: (group: VariableGroup, name: string, value: string) => void
  /** Called with the group's `zoneId` when the user clicks Retry on a failed def load. */
  onRetry?: (zoneId: string) => void
}

interface CommitFieldProps {
  group: VariableGroup
  name: string
  meta: VariableMeta
  initial: string
  /**
   * True when the key is explicitly present in `group.values` (i.e. in
   * `BakedBlockInstance.props`). Distinguishes "explicit empty" (key present,
   * value='') from "absent, uses default" (key absent). With the Object.hasOwn
   * bake semantics, explicit-empty bakes to '' (prints empty), so no
   * "will print as {name}" amber dot should fire for that case.
   */
  hasExplicitValue: boolean
  onCommit: (group: VariableGroup, name: string, value: string) => void
}

/**
 * True when the pipeline will print the literal `{name}` placeholder — i.e.
 * the key is NOT explicitly set in props AND there is no declared default.
 * With explicit-empty semantics (Object.hasOwn in bake-block.ts):
 *   - key present with '' → bakes to '' → prints empty → NOT unresolved
 *   - key absent + default '' or absent → bakes to {name} → IS unresolved
 */
function isUnresolved(meta: VariableMeta, hasExplicitValue: boolean, initial: string): boolean {
  // If the user has explicitly set the value (even to empty), the pipeline
  // will print '' — not the `{name}` placeholder. No dot.
  if (hasExplicitValue) return false
  // Key absent: will use the default if one exists, otherwise fall through to {name}.
  const effectiveDefault = (meta.default ?? '').trim()
  // Also treat initial as the effective value when key absent (e.g. pre-bake fill).
  const effectiveValue = initial.trim()
  return effectiveValue === '' && effectiveDefault === ''
}

/**
 * One field with local edit state. Text-like controls commit on blur/Enter;
 * selects (enum/boolean) commit immediately on change. Re-seeds from `initial`
 * whenever the committed value changes upstream (e.g. after re-bake or undo).
 */
function CommitField({ group, name, meta, initial, hasExplicitValue, onCommit }: CommitFieldProps) {
  const { t } = useTranslation()
  const [val, setVal] = useState(initial)
  useEffect(() => {
    setVal(initial)
  }, [initial])

  const immediate = meta.type === 'enum' || meta.type === 'boolean'
  const commit = (next: string) => {
    if (next !== initial) onCommit(group, name, next)
  }

  const unresolved = isUnresolved(meta, hasExplicitValue, initial)

  return (
    <div>
      <div className="flex items-center gap-1">
        {unresolved && (
          <span
            data-testid={`unresolved-dot-${name}`}
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-amber-400"
            aria-label={t('templateDesigner.propertyPanel.unresolvedVariable')}
          />
        )}
        <VariableField
          name={name}
          meta={meta}
          value={val}
          onChange={next => {
            setVal(next)
            if (immediate) commit(next)
          }}
          onBlur={() => commit(val)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
        />
      </div>
      {unresolved && (
        <p className="mt-0.5 text-[11px] text-amber-600">
          {t('templateDesigner.propertyPanel.willPrintAs', { placeholder: `{${name}}` })}
        </p>
      )}
    </div>
  )
}

/**
 * Compute heading index suffixes for groups whose FULL heading would collide:
 * same blockName+version AND same placementLabel (including "no label").
 * A placementLabel that differs between duplicates already disambiguates them
 * (no index); duplicates that share the same label (e.g. two instances both at
 * the default C/C alignment) fall back to (2), (3), … in stable zone-tree
 * order. First occurrence of a colliding key gets no suffix.
 */
function computeHeadingIndices(groups: VariableGroup[]): Map<string, number> {
  const keyOf = (g: VariableGroup) =>
    `${g.blockName}·v${g.blockVersion}·${g.placementLabel ?? ''}`
  // First pass: count total occurrences per full-heading key.
  const totalCount = new Map<string, number>()
  for (const g of groups) {
    const key = keyOf(g)
    totalCount.set(key, (totalCount.get(key) ?? 0) + 1)
  }
  // Second pass: assign 1-based occurrence index; first occurrence keeps no
  // suffix, subsequent ones get their occurrence number ((2), (3), …).
  const seen = new Map<string, number>()
  const result = new Map<string, number>()
  for (const g of groups) {
    const key = keyOf(g)
    if ((totalCount.get(key) ?? 1) <= 1) continue // unique heading — no index
    const occurrence = (seen.get(key) ?? 0) + 1
    seen.set(key, occurrence)
    if (occurrence >= 2) result.set(g.zoneId, occurrence)
  }
  return result
}

export function TemplateVariablesForm({ groups, onCommit, onRetry }: TemplateVariablesFormProps) {
  const headingIndices = computeHeadingIndices(groups)

  return (
    <div data-testid="template-variables-form" className="space-y-4">
      {groups.map(group => {
        const base = group.placementLabel
          ? `${group.blockName} · v${group.blockVersion} · ${group.placementLabel}`
          : `${group.blockName} · v${group.blockVersion}`
        const idx = headingIndices.get(group.zoneId)
        const heading = idx ? `${base} (${idx})` : base

        const entries = group.variables ? Object.entries(group.variables) : []
        return (
          <section key={group.zoneId} data-testid={`tvf-group-${group.zoneId}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              {heading}
            </p>
            {group.variables === undefined ? (
              <div className="mt-1">
                <p className="text-xs text-stone-400">
                  Couldn&apos;t load this block&apos;s variables.
                </p>
                {group.loadFailed && onRetry && (
                  <button
                    type="button"
                    className="mt-1 text-xs text-sky-600 hover:text-sky-700 underline"
                    onClick={() => onRetry(group.zoneId)}
                  >
                    Retry
                  </button>
                )}
              </div>
            ) : entries.length === 0 ? (
              <p className="mt-1 text-xs text-stone-400">(no variables)</p>
            ) : (
              <div className="mt-2 space-y-2">
                {entries.map(([name, meta]) => (
                  <CommitField
                    key={name}
                    group={group}
                    name={name}
                    meta={meta}
                    initial={group.values[name] ?? ''}
                    hasExplicitValue={Object.hasOwn(group.values, name)}
                    onCommit={onCommit}
                  />
                ))}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}
